/** @format */

import { Pipe, PipeTransform } from '@angular/core';

import { EMPTY_HMF_PRICES } from '@shared/totf/components';
import { HmfPrices } from '@dsg-tech/homefield-angular';

interface LegacyPrices {
	maxlistprice?: string;
	maxofferprice?: string;
	minlistprice?: string;
	minofferprice?: string;
	maxmapprice?: string;
	offerprice?: string;
}

type PriceInput = LegacyPrices | HmfPrices | null | undefined;

@Pipe({
	name: 'transformPrices',
	standalone: true,
	pure: true
})
export class TransformPricesPipe implements PipeTransform {
	/**
	 * Factory function to create a complete HmfPrices object with default values
	 * Follows the DTO pattern for safe object creation with partial data
	 * Handles null and undefined values by converting them to empty strings
	 * @param data - Partial HmfPrices data to merge with defaults
	 * @returns Complete HmfPrices object with all required properties
	 */
	private createHmfPrices(data: Partial<HmfPrices> = {}): HmfPrices {
		// Filter out null/undefined values to prevent them from overriding defaults
		const cleanData = Object.fromEntries(
			Object.entries(data).filter(
				([, value]) => value != null && typeof value !== 'undefined'
			)
		);

		return { ...EMPTY_HMF_PRICES, ...cleanData };
	}

	/**
	 * Transforms price structure to new HmfPrices format
	 * Handles both legacy (lowercase) and new (camelCase) price formats
	 * @param productPrices - Price data in either legacy or new format
	 * @returns HmfPrices object with camelCase properties
	 */
	transform(productPrices: PriceInput): HmfPrices {
		if (!productPrices) {
			return EMPTY_HMF_PRICES;
		}

		// Check if it's already in new format (camelCase) with non-null values
		const isNewFormat =
			('maxListPrice' in productPrices && productPrices.maxListPrice != null) ||
			('minOfferPrice' in productPrices && productPrices.minOfferPrice != null);

		if (isNewFormat) {
			// Already in new format, return as-is with defaults for missing properties
			const newFormatPrices = productPrices as HmfPrices;
			return this.createHmfPrices(newFormatPrices);
		}

		// Transform from legacy format (lowercase) to new format (camelCase)
		const legacyPrices = productPrices as LegacyPrices;
		return this.createHmfPrices({
			maxListPrice: legacyPrices.maxlistprice || '',
			maxOfferPrice: legacyPrices.maxofferprice || '',
			minListPrice: legacyPrices.minlistprice || '',
			minOfferPrice: legacyPrices.minofferprice || '',
			maxMapPrice: legacyPrices.maxmapprice || '',
			offerPrice: legacyPrices.offerprice || ''
		});
	}
}
